import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
} from "@mui/material";
import SoftSwitch from "components/SoftSwitch";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsIcon from "@mui/icons-material/Notifications";
import RefreshIcon from "@mui/icons-material/Refresh";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftAvatar from "components/SoftAvatar";
import { FormField } from "components/CrmFormControls";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import toast from "react-hot-toast";
import apiService from "services/api";
import {
  clearAuthSession,
  getAuthUserId,
  getAuthUserType,
} from "utils/authStorage";

const ROLE_LABELS = {
  SUPER_ADMIN: "Super Admin",
  ADMIN: "Admin",
  SUB_ADMIN: "Employee",
  USER: "User",
  SUB_USER: "Sub User",
  DELIVERY_BOY: "Delivery",
  ONBOARDING_USER: "Onboarding",
};

const ROLE_COLORS = {
  SUPER_ADMIN: "error",
  ADMIN: "info",
  SUB_ADMIN: "primary",
  USER: "default",
  SUB_USER: "default",
  DELIVERY_BOY: "warning",
  ONBOARDING_USER: "secondary",
};

const userImageUrl = (userImage) => {
  if (!userImage) return null;
  const base = process.env.REACT_APP_IMG || "";
  if (userImage.startsWith("http")) return userImage;
  return `${base.replace(/\/$/, "")}/${String(userImage).replace(/^\//, "")}`;
};

const formatPhone = (phone) => {
  if (!phone) return "—";
  const s = String(phone);
  return s.length === 10 ? `+91 ${s.slice(0, 5)} ${s.slice(5)}` : s;
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatRole = (userType) => {
  const key = String(userType || "").toUpperCase();
  return ROLE_LABELS[key] || key.replace(/_/g, " ") || "User";
};

function DetailRow({ icon, label, value }) {
  return (
    <SoftBox display="flex" alignItems="flex-start" py={1.25}>
      <SoftBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="2rem"
        height="2rem"
        borderRadius="md"
        bgColor="grey-100"
        color="dark"
        mr={1.5}
        flexShrink={0}
      >
        {icon}
      </SoftBox>
      <SoftBox flex={1} minWidth={0}>
        <SoftTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
          {label}
        </SoftTypography>
        <SoftTypography variant="button" fontWeight="regular" color="dark" display="block">
          {value || "—"}
        </SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}

function Profile() {
  const navigate = useNavigate();
  const userId = getAuthUserId();
  const storedRole = getAuthUserType();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
  });

  const fetchProfile = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.getUser(userId);
      const result = response?.data;
      if (result?.success && result?.data) {
        setUser(result.data);
      } else {
        setUser(null);
        toast.error(result?.message || "Failed to load profile");
      }
    } catch (err) {
      setUser(null);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const avatarSrc = useMemo(() => userImageUrl(user?.userImage), [user?.userImage]);

  const roleLabel = formatRole(user?.userType || storedRole);
  const roleColor = ROLE_COLORS[String(user?.userType || storedRole).toUpperCase()] || "default";

  const openEditDialog = () => {
    setFormData({
      fullName: user?.fullName || "",
      email: user?.email || "",
      phone: user?.phone ? String(user.phone) : "",
      password: "",
    });
    setShowEditDialog(true);
  };

  const handleFormChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveProfile = async () => {
    if (!userId) return;

    const payload = {
      fullName: formData.fullName.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      notification: user?.notification ?? true,
    };

    if (formData.password.trim()) {
      payload.password = formData.password.trim();
    }

    try {
      setSaving(true);
      const response = await apiService.updateUser(userId, payload);
      const result = response?.data;
      if (result?.success) {
        toast.success(result?.message || "Profile updated");
        setShowEditDialog(false);
        await fetchProfile();
      } else {
        toast.error(result?.message || "Failed to update profile");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationToggle = async () => {
    if (!userId || !user) return;

    const nextValue = !user.notification;
    try {
      const response = await apiService.updateUser(userId, {
        fullName: user.fullName,
        email: user.email,
        phone: user.phone,
        notification: nextValue,
      });
      const result = response?.data;
      if (result?.success) {
        setUser((prev) => ({ ...prev, notification: nextValue }));
        toast.success(nextValue ? "Notifications enabled" : "Notifications disabled");
      } else {
        toast.error(result?.message || "Failed to update notification setting");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to update notification setting");
    }
  };

  const handleLogout = () => {
    clearAuthSession();
    navigate("/login");
    toast.success("Logged out successfully");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">
              My Profile
            </SoftTypography>
            <SoftTypography variant="button" color="text" fontWeight="regular">
              View and manage your account details
            </SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1}>
            <SoftButton variant="outlined" color="dark" onClick={fetchProfile} disabled={loading}>
              <RefreshIcon sx={{ mr: 0.5, fontSize: "1.1rem !important" }} />
              Refresh
            </SoftButton>
            <SoftButton variant="gradient" color="info" onClick={openEditDialog} disabled={loading || !user}>
              <EditIcon sx={{ mr: 0.5, fontSize: "1.1rem !important" }} />
              Edit Profile
            </SoftButton>
          </SoftBox>
        </SoftBox>

        {loading ? (
          <Card>
            <SoftBox display="flex" justifyContent="center" alignItems="center" py={8}>
              <CircularProgress color="info" />
            </SoftBox>
          </Card>
        ) : !user ? (
          <Card>
            <SoftBox py={6} px={3} textAlign="center">
              <SoftTypography variant="h6" color="text">
                Unable to load profile. Please sign in again.
              </SoftTypography>
            </SoftBox>
          </Card>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card
                sx={{
                  overflow: "hidden",
                  background: ({ palette }) =>
                    `linear-gradient(135deg, ${palette.info.main} 0%, ${palette.info.dark || palette.info.main} 100%)`,
                }}
              >
                <SoftBox p={3} display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                  <SoftAvatar
                    src={avatarSrc}
                    alt={user.fullName || "Profile"}
                    variant="rounded"
                    size="xxl"
                    shadow="md"
                    bgColor="light"
                    sx={{
                      border: "3px solid rgba(255,255,255,0.9)",
                      "& .MuiAvatar-img": { objectFit: "cover" },
                    }}
                  />
                  <SoftBox flex={1} minWidth={0}>
                    <SoftTypography variant="h4" fontWeight="bold" color="white">
                      {user.fullName || "Unnamed User"}
                    </SoftTypography>
                    <SoftBox display="flex" alignItems="center" flexWrap="wrap" gap={1} mt={0.5}>
                      <Chip
                        label={roleLabel}
                        color={roleColor}
                        size="small"
                        sx={{ fontWeight: 600, bgcolor: "rgba(255,255,255,0.95)" }}
                      />
                      <Chip
                        label={user.disable ? "Disabled" : "Active"}
                        color={user.disable ? "error" : "success"}
                        size="small"
                        variant="outlined"
                        sx={{ color: "white", borderColor: "rgba(255,255,255,0.7)" }}
                      />
                    </SoftBox>
                    {user.email && (
                      <SoftTypography variant="button" color="white" opacity={0.9} display="block" mt={1}>
                        {user.email}
                      </SoftTypography>
                    )}
                  </SoftBox>
                </SoftBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={7}>
              <Card sx={{ height: "100%" }}>
                <SoftBox pt={2.5} px={2.5} pb={1}>
                  <SoftTypography variant="h6" fontWeight="medium">
                    Account Information
                  </SoftTypography>
                  <SoftTypography variant="button" color="text" fontWeight="regular">
                    Your personal and login details
                  </SoftTypography>
                </SoftBox>
                <SoftBox px={2.5} pb={2.5}>
                  <Divider sx={{ my: 1.5 }} />
                  <DetailRow
                    icon={<BadgeIcon fontSize="small" />}
                    label="Full Name"
                    value={user.fullName}
                  />
                  <DetailRow
                    icon={<EmailIcon fontSize="small" />}
                    label="Email"
                    value={user.email}
                  />
                  <DetailRow
                    icon={<PhoneIcon fontSize="small" />}
                    label="Phone"
                    value={formatPhone(user.phone)}
                  />
                  <DetailRow
                    icon={<CalendarTodayIcon fontSize="small" />}
                    label="Member Since"
                    value={formatDate(user.createdAt)}
                  />
                  <DetailRow
                    icon={<CalendarTodayIcon fontSize="small" />}
                    label="Last Updated"
                    value={formatDate(user.updatedAt)}
                  />
                </SoftBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card sx={{ height: "100%" }}>
                <SoftBox pt={2.5} px={2.5} pb={1}>
                  <SoftTypography variant="h6" fontWeight="medium">
                    Account Settings
                  </SoftTypography>
                  <SoftTypography variant="button" color="text" fontWeight="regular">
                    Manage preferences and session
                  </SoftTypography>
                </SoftBox>
                <SoftBox px={2.5} pb={2.5}>
                  <Divider sx={{ my: 1.5 }} />
                  <SoftBox display="flex" alignItems="center" py={1.5}>
                    <SoftBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width="2rem"
                      height="2rem"
                      borderRadius="md"
                      bgColor="grey-100"
                      color="dark"
                      mr={1.5}
                    >
                      <NotificationsIcon fontSize="small" />
                    </SoftBox>
                    <SoftBox flex={1}>
                      <SoftTypography variant="button" fontWeight="medium" color="dark">
                        Push Notifications
                      </SoftTypography>
                      <SoftTypography variant="caption" color="text" display="block">
                        Receive in-app alerts and updates
                      </SoftTypography>
                    </SoftBox>
                    <SoftSwitch
                      checked={Boolean(user.notification)}
                      onChange={handleNotificationToggle}
                      color="info"
                    />
                  </SoftBox>

                  <Divider sx={{ my: 1.5 }} />

                  <SoftBox mt={2}>
                    <SoftButton
                      variant="outlined"
                      color="error"
                      fullWidth
                      onClick={handleLogout}
                      startIcon={<LogoutIcon />}
                    >
                      Log Out
                    </SoftButton>
                  </SoftBox>
                </SoftBox>
              </Card>
            </Grid>
          </Grid>
        )}
      </SoftBox>

      <Dialog open={showEditDialog} onClose={() => !saving && setShowEditDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent dividers>
          <Box component="form" sx={{ pt: 0.5 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormField label="Full Name">
                  <SoftInput
                    placeholder="Full name"
                    value={formData.fullName}
                    onChange={handleFormChange("fullName")}
                  />
                </FormField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormField label="Email">
                  <SoftInput
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleFormChange("email")}
                  />
                </FormField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormField label="Phone">
                  <SoftInput
                    placeholder="10-digit phone"
                    value={formData.phone}
                    onChange={handleFormChange("phone")}
                  />
                </FormField>
              </Grid>
              <Grid item xs={12}>
                <FormField label="New Password (optional)">
                  <SoftInput
                    type="password"
                    placeholder="Leave blank to keep current password"
                    value={formData.password}
                    onChange={handleFormChange("password")}
                  />
                </FormField>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <SoftButton variant="outlined" color="dark" onClick={() => setShowEditDialog(false)} disabled={saving}>
            Cancel
          </SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSaveProfile} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Profile;
